<template>
  <div class="container">
    <div class="row">
      <div class="overviewAndNav mb-1">
        <h3 class="d-inline-block">Gedetailleerd overzicht van gebruiker {{ selectedUserDetail.firstName }}
          {{ selectedUserDetail.lastName }}</h3>
        <UpdateIcon v-if="!isUpdateMode" @clicked="toggleUpdateMode"/>
      </div>
    </div>
    <div class="row d-flex justify-content-center text-center my-3">
      <section>
        <TerminateUserModal/>
      </section>
      <section>
        <button
            v-if="!isProlongedSickLeavePeriodStarted()" class="btn-basic btn-fill-pulse-dark"
            @click="showProlongedSickLeaveModal"
            style="width: 180px;">
          Op langdurig ziekte zetten
        </button>
        <button
            v-if="isProlongedSickLeavePeriodStarted()" class="btn-basic btn-fill-pulse"
            style="width: 180px;"
            @click="showProlongedSickLeaveModal">
          Uit langdurig ziekte zetten
        </button>
      </section>
      <section>
        <ProlongedSickLeaveModal
            :isProlongedSickLeavePeriodStarted="isProlongedSickLeavePeriodStarted()"
            @close="closeProlongedSickLeaveModal"
            @start-period="startProlongSickLeave"
            @end-period="endProlongSickLeave"/>
      </section>
    </div>

    <div class="row">
      <div class="col-lg-6 text-right">
        <div class="text-left d-inline-block">
          <p class="py-2">Id</p>
          <p class="py-2">Werknemersnummer</p>
          <p class="py-2">Email</p>
          <p class="py-2">Voornaam</p>
          <p class="py-2">Achternaam</p>
          <p class="py-2">Deeltijds percentage</p>
          <p class="py-2">Provider</p>
          <p class="py-2">Rol</p>
          <p class="py-2">HR Werkgever</p>
          <p class="py-2">Duur absentiedag</p>
          <p class="py-2">Start datum</p>
          <p v-if="selectedUserDetail.employmentEndDate" class="py-2">Datum einde tewerkstelling</p>
          <p class="py-2">Is langdurig ziek</p>
        </div>
      </div>
      <div class="col-lg-6 text-left">
        <ValidationObserver
            v-slot="{ invalid, handleSubmit }"
            slim>
          <form @submit.prevent="handleSubmit(saveFormConfirmation)">
            <p class="py-2">{{ selectedUserDetail.id }}</p>

            <NumberInputField
                v-if="isUpdateMode"
                class="mt-3_2 negative-ml" v-model="updatedEmployeeNr"
                inline
                labelText="werknemersnummer"
                minValue="0" maxValue="9999999"/>
            <p class="py-2" v-else>{{ getEmployeeNumberText() }}</p>

            <TextInputField
                v-if="isUpdateMode"
                class="mt-3_2 negative-ml" v-model="updatedEmail"
                labelText="email" required
                email inline/>
            <p class="py-2" v-else>{{ selectedUserDetail.email }}</p>

            <TextInputField
                v-if="isUpdateMode"
                class="mt-3_5 negative-ml" v-model="updatedFirstName"
                labelText="voornaam" required
                inline/>
            <p class="py-2" v-else>{{ selectedUserDetail.firstName }}</p>

            <TextInputField
                v-if="isUpdateMode"
                class="mt-3_5 negative-ml" v-model="updatedLastName"
                labelText="achternaam" required
                inline/>
            <p class="py-2" v-else>{{ selectedUserDetail.lastName }}</p>

            <p class="py-2"> {{ getCurrentPartTimePercentage() }}</p>

            <ValidationProvider
                v-if="isUpdateMode"
                tag="div" class="mt-4 negative-ml d-flex flex-row"
                name="provider" rules="required"
                v-slot="{ errors }">
              <div class="form-check  mr-3">
                <input
                    class="form-check-input" type="radio"
                    id="google" value="GOOGLE"
                    v-model="updatedAuthProvider"/>
                <label class="form-check-label" for="google">Google</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input" type="radio"
                    id="apple" value="APPLE"
                    v-model="updatedAuthProvider"/>
                <label class="form-check-label" for="apple">Apple</label>
              </div>
              <div class="pl-3 col-lg-8">
                <span class="text-red">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <p class="py-2" v-else>{{ capitalize(selectedUserDetail.authProvider) }}</p>

            <ValidationProvider
                v-if="isUpdateMode"
                tag="div" class="mt-3_8 negative-ml d-flex flex-row"
                name="rol" rules="required"
                v-slot="{ errors }">
              <div class="form-check mr-3">
                <input
                    class="form-check-input" type="checkbox"
                    id="admin" value="ROLE_ADMIN"
                    v-model="updatedRoles"/>
                <label class="form-check-label" for="admin">Admin</label>
              </div>

              <div class="form-check">
                <input
                    class="form-check-input" type="checkbox"
                    id="user" value="ROLE_USER"
                    v-model="updatedRoles"/>
                <label class="form-check-label" for="user">Gebruiker</label>
              </div>
              <div class="pl-3 col-lg-8">
                <span class="text-red">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
            <p class="py-2" v-else>{{ toReadableFormat(selectedUserDetail.roles) }}</p>

            <label class="mt-4 negative-ml" v-if="isUpdateMode">
              <select v-if="selectedUserDetail.hrEmployer && hrEmployers.length>1" v-model="updatedHREmployerID"
                      @change="OnHREmployerChange()" class="form-control">
                <option
                    v-for="hrEmployer in hrEmployers"
                    :selected="hrEmployer.id === 1"
                    :key="hrEmployer.id"
                    :value="hrEmployer.id">{{ hrEmployer.name }}
                </option>
              </select>
              <select v-else-if="hrEmployers.length>1 || !selectedUserDetail.hrEmployer" v-model="updatedHREmployerID"
                      @change="OnHREmployerChange()" class="form-control">
                <option :disabled="true" value="" selected>Kies een HR Werkgever</option>
                <option
                    v-for="hrEmployer in hrEmployers"
                    :key="hrEmployer.id"
                    :value="hrEmployer.id">{{ hrEmployer.name }}
                </option>
              </select>
              <p class="py-2" v-else>{{
                  selectedUserDetail.hrEmployer == null ? "/" : selectedUserDetail.hrEmployer.name
                }}</p>

            </label>
            <p class="py-2" v-else>{{
                selectedUserDetail.hrEmployer == null ? "/" : selectedUserDetail.hrEmployer.name
              }}</p>


            <NumberInputField
                v-if="isUpdateMode"
                class="my-3 negative-ml" v-model="updatedSickDayHours"
                step=".1" labelText="duur absentiedag"
                required inline
                minValue="0" maxValue="24"/>
            <p class="py-2" v-else>{{ selectedUserDetail.sickDayHours }}</p>
            <DateInputField
                v-if="isUpdateMode"
                class="mt-3_5_ mb-4 negative-ml col-lg-4"
                v-model="updatedEmploymentStartDate"
                :no-label="true"
                :rule="endDateFormatted()" required/>
            <p class="py-2" v-else>{{ selectedUserDetail.employmentStartDate }}</p>
            <p v-if="selectedUserDetail.employmentEndDate" class="py-2">{{ selectedUserDetail.employmentEndDate }}</p>
            <p v-if="selectedUserDetail.onProlongedSickLeave" class="py-2">Ja</p>
            <p v-else class="py-2">Nee</p>

            <div v-if="isUpdateMode" class="form-row align-items-center py-2">
              <div class="input-group col-lg-4 d-flex flex-row-reverse">
                <button
                    type="submit" :disabled="invalid"
                    class="remove-button-look">
                  <SaveIcon/>
                </button>
                <CancelIcon class="mr-3" @clicked="toggleUpdateMode"/>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
    <div class="row d-flex justify-content-around align-items-center mt-5">
      <button class="btn-basic invisible"></button>
      <h5 class="display-4" style="font-size: 1.5em;">Contracten</h5>
      <button
          v-if="showCreateContract"
          class="btn-basic btn-fill-pulse-dark" @click="toggleCreateContract">Annuleren
      </button>
      <button
          v-else
          class="btn-basic btn-fill-pulse" @click="toggleCreateContract">Toevoegen
      </button>
    </div>
    <div class="row" v-show="showCreateContract">
      <CreateContractFromUserComponent
          :userId="selectedUserDetail.id" @contractAdded="toggleCreateContract"/>
    </div>
    <div class="row mt-4">
      <table class="table">
        <thead class="thead-dark">
        <tr>
          <SortableTableHeaderComponent
              columnName="id" :sortColumn="currentSortColumn"
              :sortDirectionAscending="currentSortDirectionAscending" :sortFunction="sort"
              text="Contract id"/>
          <SortableTableHeaderComponent
              columnName="customerId" :sortColumn="currentSortColumn"
              :sortDirectionAscending="currentSortDirectionAscending" :sortFunction="sort"
              text="Klant id"/>
          <SortableTableHeaderComponent
              columnName="customerName" :sortColumn="currentSortColumn"
              :sortDirectionAscending="currentSortDirectionAscending" :sortFunction="sort"
              text="Klantnaam"/>
          <SortableTableHeaderComponent
              columnName="contractPeriod" :sortColumn="currentSortColumn"
              :sortDirectionAscending="currentSortDirectionAscending" :sortFunction="sort"
              text="Contract periode"/>
          <th scope="col">Acties</th>
        </tr>
        </thead>
        <template v-for="contractSummaryDto in sortedContracts">
          <ContractTableRowComponent
              :key="contractSummaryDto.id" :contractSummaryDto="contractSummaryDto"
              from="userDetail"/>
        </template>
      </table>
    </div>
    <div class="row d-flex justify-content-around align-items-center">
      <h5 class="display-4" style="font-size: 1.5em;">Overzicht recup</h5>
    </div>
    <div class="row">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead class="thead-dark">
          <tr>
            <th>Maand</th>
            <th>Gepresteerde uren</th>
            <th>Vereiste uren</th>
            <th>Verschil</th>
            <th>Opgebouwde recup</th>
          </tr>
          </thead>
          <template v-for="(recupForMonthDto, index) in recupForYearDto">
            <tr :key='index'>
              <td>{{ recupForMonthDto.yearMonth }}</td>
              <td>{{ recupForMonthDto.workedHours }}</td>
              <td>{{ recupForMonthDto.neededHours }}</td>
              <td>{{ recupForMonthDto.differenceHours }}</td>
              <td>{{ recupForMonthDto.cumulativeHours }}</td>
            </tr>
          </template>
        </table>
      </div>
    </div>
    <div class="row">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead class="thead-dark">
          <tr>
            <th>Datum</th>
            <th>Uren</th>
            <th>Reden</th>
            <th v-if="showCreateRecupBonus"></th>
          </tr>
          </thead>
          <template v-for="(recupBonus, index) in recupBonuses">
            <tr :key='index'>
              <td>{{ recupBonus.date }}</td>
              <td>{{ recupBonus.hours }}</td>
              <td>{{ recupBonus.reason }}</td>
              <td v-if="showCreateRecupBonus"><button class="btn-basic btn-danger" @click="removeRecupBonus(recupBonus.date)">Verwijderen</button></td>
            </tr>
          </template>
        </table>
        <div class="mt-5">
          <button
              v-if="showCreateRecupBonus"
              class="btn-basic btn-fill-pulse-dark" @click="toggleCreateRecupBonus">Annuleren
          </button>
          <button
              v-else
              class="btn-basic btn-fill-pulse" @click="toggleCreateRecupBonus">Aanpassen
          </button>
        </div>
        <div class="row" v-show="showCreateRecupBonus">
          <createRecupBonus/>
        </div>
      </div>
    </div>
    <div class="row d-flex justify-content-around align-items-center mt-5">
      <button class="btn-basic invisible"></button>
      <h5 class="display-4" style="font-size: 1.5em;"> Geschiedenis deeltijds percentage </h5>
      <button
          v-if="showCreatePartTimePercentageRecord"
          class="btn-basic btn-fill-pulse-dark" @click="toggleCreatePartTimePercentageRecord">Annuleren
      </button>
      <button
          v-else
          class="btn-basic btn-fill-pulse" @click="toggleCreatePartTimePercentageRecord">Toevoegen
      </button>
    </div>

    <div class="row" v-show="showCreatePartTimePercentageRecord">
      <CreatePartTimePercentageRecordComponent
          @close-create-part-time-percentage-record="closePartTimePercentageRecord"
          :part-time-percentage-records="selectedUserDetail.partTimePercentageRecords"/>
    </div>

    <div class="row mt-5">
      <table class="table">
        <thead class="thead-dark">
        <tr>
          <SortableTableHeaderComponent
              columnName="percentage"
              text="Percentage"/>
          <SortableTableHeaderComponent
              columnName="period"
              text="Periode"/>
          <th scope="col">Acties</th>
        </tr>
        </thead>
        <template v-for="(partTimePercentageRecord,index) in selectedUserDetail.partTimePercentageRecords">
          <PartTimePercentageTableRowComponent
              :key="partTimePercentageRecord.id"
              :part-time-percentage="partTimePercentageRecord"
              :prev-element="getPrevElement(index)"
              :next-element="getNextElement(index)"
              @update-prev-element="updatePrevElement(index, $event)"
              @update-next-element="updateNextElement(index, $event)"
              @update-current-element="updateCurrentElement(index, $event)"/>
        </template>
      </table>
    </div>

    <div class="row d-block">
      <h5 class="mt-5" style="font-size: 1.5em;"> Geschiedenis langdurige ziekte </h5>
    </div>

    <div class="row mt-4">
      <table class="table">
        <thead class="thead-dark">
        <tr>
          <SortableTableHeaderComponent
              sort-column="startDate"
              :sort-direction-ascending="currentSortDirectionAscending"
              :sortFunction="sort"
              columnName="startDate"
              text="Start datum"/>
          <SortableTableHeaderComponent
              sort-column="endDate"
              :sort-direction-ascending="currentSortDirectionAscending"
              :sortFunction="sort"
              columnName="endDate"
              text="Eind datum"/>
          <th scope="col">Acties</th>
        </tr>
        </thead>
        <template v-for="(prolongedSickLeavePeriod) in sortProlongedSickLeavePeriods">
          <ProlongedSickLeavePeriodHistoryTableRowComponent
              :key="prolongedSickLeavePeriod.id"
              :prolonged-sick-leave-period="prolongedSickLeavePeriod"
              :prolonged-sick-leave-periods-list="sortProlongedSickLeavePeriods"/>
        </template>
      </table>
    </div>

    <div class="row d-flex justify-content-around align-items-center mt-5">
      <h5 class="display-4" style="font-size: 1.5em;">Vakantiesaldo's huidig jaar</h5>
    </div>

    <div class="row mt-4">
      <table class="table">
        <thead class="thead-dark">
        <tr>
          <th>Type vakantie</th>
          <th>Huidig saldo</th>
          <th>Oorspronkelijk saldo</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="vacationType in vacationTypes" v-bind:key="vacationType.id">
          <td>{{ vacationType.typeHumanReadableFormat }}</td>
          <td>{{ vacationType.currentBalance }}</td>
          <td>{{ vacationType.originalBalance }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>


import {mapActions, mapGetters} from "vuex";
import CreateContractFromUserComponent from "@/Admin/CreateContractComponent/CreateContractFromUserComponent";
import {listOfStringToConcatenatedString} from "@/util/FormattingUtil";
import SaveIcon from "@/components/icons/Save";
import CancelIcon from "@/components/icons/Ban";
import UpdateIcon from "@/components/icons/Pencil";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import ContractTableRowComponent from "@/Admin/ContractTableRowComponent/ContractTableRowComponent";
import PartTimePercentageTableRowComponent
  from "@/Admin/PartTimePercentageTableRowComponent/PartTimePercentageTableRowComponent.vue";
import {sortListByColumnAndDirection} from "@/util/SortingUtil";
import SortableTableHeaderComponent from "@/components/tables/SortableTableHeader";
import TextInputField from "@/components/inputFields/TextInputField";
import NumberInputField from "@/components/inputFields/NumberInputField";
import {toYearAndMonthAndDay} from "@/util/DateUtil";
import TerminateUserModal from "@/components/modals/TerminateUserModal.vue";
import ProlongedSickLeaveModal from "../components/modals/ProlongedSickLeaveModal.vue";
import {VoerroModal} from "@voerro/vue-modal";
import DateInputField from "@/components/inputFields/DateInputField.vue";
import ProlongedSickLeavePeriodHistoryTableRowComponent
  from "@/Admin/ProlongedSickLeavePeriodHistoryTableRowComponent/ProlongedSickLeavePeriodHistoryTableRowComponent.vue";
import CreatePartTimePercentageRecordComponent
  from "@/Admin/CreatePartTimePercentageRecordComponent/CreatePartTimePercentageRecordComponent.vue";
import createRecupBonus from "../Admin/createRecupBonusComponent/CreateRecupBonus.vue";


export default {
  name: "AdminUserDetail",
  components: {
    createRecupBonus,
    ProlongedSickLeavePeriodHistoryTableRowComponent,
    CreatePartTimePercentageRecordComponent,
    DateInputField,
    TerminateUserModal,
    NumberInputField,
    TextInputField,
    SortableTableHeaderComponent,
    ContractTableRowComponent,
    PartTimePercentageTableRowComponent,
    UpdateIcon,
    CancelIcon,
    SaveIcon,
    CreateContractFromUserComponent,
    ValidationProvider,
    ValidationObserver,
    ProlongedSickLeaveModal
  },
  data: () => ({
    isUpdateMode: false,
    isChangingActiveState: false,
    showCreateShiftType: false,
    showCreateContract: false,
    showCreatePartTimePercentageRecord: false,
    showCreateRecupBonus: false,
    updatedEmail: "",
    updatedFirstName: "",
    updatedLastName: "",
    updatedAuthProvider: "",
    updatedRoles: [],
    updatedHREmployerID: "",
    updatedEmployeeNr: "",
    updatedSickDayHours: "",
    updatedEmploymentStartDate: "",
    currentSortColumn: "contractPeriod",
    currentSortDirectionAscending: true,
    workedYearly: [],
    isProlongedSickLeavePeriodAlreadyClosed: false
  }),
  computed: {
    ...mapGetters({
      selectedUserDetail: "administrationUser/selectedUserDetail",
      recupWithDeltas: "administrationUser/recupWithDeltas",
      vacationTypes: "administrationUser/vacationTypes",
      hrEmployers: "administrationUser/hrEmployers",
      plannedShifts: "timesheet/plannedShifts",
      workedEnough: "timesheet/workedEnough",
      countedHours: "timesheet/countedHours",
      neededHours: "timesheet/neededHours",
      currentlySelectedMonth: "time/currentlySelectedMonth",
      workedHours: "administrationTimeSheet/workedHoursThisYear",
      recupForYearDto: "administrationUser/recupForYearDto",
      recupBonuses: "administrationUser/recupBonuses"

    }),
    sortedContracts() {
      const contracts = [...this.selectedUserDetail.contractSummaryDtos || []];
      if (this.currentSortColumn === "contractPeriod") {
        if (this.currentSortDirectionAscending) {
          return contracts;
        }
        return contracts.reverse();
      }
      return sortListByColumnAndDirection(contracts, this.currentSortColumn, this.currentSortDirectionAscending);
    },
    sortProlongedSickLeavePeriods() {
      return sortListByColumnAndDirection(this.selectedUserDetail.prolongedSickLeavePeriodSet, "startDate", this.currentSortDirectionAscending);
    }
  },
  async created() {
    await this.fetchRecupForYear();
    await this.fetchDetailsForSelectedUser();
    await this.fetchHREmployers();
    await this.fetchVacationForSelectedUserAndCurrentYear();
  },
  methods: {
    ...mapActions("administrationUser", [
      "fetchDetailsForSelectedUser",
      "fetchVacationForSelectedUserAndCurrentYear",
      "fetchHREmployers",
      "updateLastRecupForSelectedUser",
      "updateUser",
      "startProlongedSickLeaveForSelectedUser",
      "endProlongedSickLeaveForSelectedUser",
      "activateUser",
      "fetchRecupForYear",
      "deleteRecupBonus"
    ]),
    getPrevElement(index) {
      return index > 0 ? this.selectedUserDetail.partTimePercentageRecords[index - 1] : null;
    },
    getNextElement(index) {
      return index < this.selectedUserDetail.partTimePercentageRecords.length - 1 ? this.selectedUserDetail.partTimePercentageRecords[index + 1] : null;
    },
    updatePrevElement(index, newPrevElement) {
      if (index > 0) {
        this.$set(this.selectedUserDetail.partTimePercentageRecords, index - 1, newPrevElement);
      }
    },
    updateNextElement(index, newNextElement) {
      if (index < this.selectedUserDetail.partTimePercentageRecords.length - 1) {
        this.$set(this.selectedUserDetail.partTimePercentageRecords, index + 1, newNextElement);
      }
    },
    updateCurrentElement(index, newElement) {
      this.$set(this.selectedUserDetail.partTimePercentageRecords, index, newElement);
    },
    hasUnconfirmedRecup() {
      return (this.recupWithDeltas.recupDeltaDtoList || []).length;
    },
    toggleCreateContract() {
      this.showCreateContract = !this.showCreateContract;
    },
    toggleCreateRecupBonus() {
      this.showCreateRecupBonus = !this.showCreateRecupBonus;
    },
    removeRecupBonus(date){
      this.deleteRecupBonus(date);
    },
    toggleCreatePartTimePercentageRecord() {
      this.showCreatePartTimePercentageRecord = !this.showCreatePartTimePercentageRecord;
    },
    closePartTimePercentageRecord() {
      this.showCreatePartTimePercentageRecord = false;
    },
    toggleUpdateMode() {
      this.updatedEmail = this.selectedUserDetail.email;
      this.updatedFirstName = this.selectedUserDetail.firstName;
      this.updatedLastName = this.selectedUserDetail.lastName;
      this.updatedAuthProvider = this.selectedUserDetail.authProvider;
      this.updatedRoles = this.selectedUserDetail.roles;
      this.updatedEmployeeNr = this.selectedUserDetail.employeeNr;
      this.updatedSickDayHours = this.selectedUserDetail.sickDayHours;
      this.updatedEmploymentStartDate = this.selectedUserDetail.employmentStartDate;

      if (this.selectedUserDetail.hrEmployer) {
        this.updatedHREmployerID = this.selectedUserDetail.hrEmployer.id;
      }

      this.isUpdateMode = !this.isUpdateMode;
    },
    saveFormConfirmation() {
      if (confirm('Bent u zeker dat u deze wijzigingen wilt opslaan?')) {
        this.saveFormConfirmed();
      }
    },
    getCurrentPartTimePercentage() {
      const records = this.selectedUserDetail.partTimePercentageRecords;
      const today = toYearAndMonthAndDay(new Date());
      for (let record of records) {
        const startDate = toYearAndMonthAndDay(record.startDate);
        const endDAte = toYearAndMonthAndDay(record.endDate);
        if (today >= startDate && (today <= endDAte || record.endDate === null)) {
          return record.percentage;
        }
      }
      return records[records.length - 1].percentage;
    },
    saveFormConfirmed() {
      const updateUserDto = {
        email: this.updatedEmail,
        firstName: this.updatedFirstName,
        lastName: this.updatedLastName,
        provider: this.updatedAuthProvider,
        roles: this.updatedRoles,
        employeeNr: this.updatedEmployeeNr,
        sickDayHours: this.updatedSickDayHours,
        employmentStartDate: this.updatedEmploymentStartDate,
        onProlongedSickLeave: this.selectedUserDetail.onProlongedSickLeave,
        hrEmployerId: this.updatedHREmployerID === "" && !this.selectedUserDetail.hrEmployer ? null : this.updatedHREmployerID
      };
      this.updateUser(updateUserDto)
          .then(() => this.toggleUpdateMode());
    },
    toReadableFormat(list) {
      if (!list) {
        return "";
      }
      const listWithFormattedRoles = list
          .map(role => role.replace("ROLE_", ""))
          .map(role => role.charAt(0).toUpperCase() + role.slice(1).toLowerCase());
      return listOfStringToConcatenatedString(listWithFormattedRoles);
    },
    capitalize(value) {
      if (!value) {
        return "";
      }
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    },
    getEmployeeNumberText() {
      if (this.selectedUserDetail.employeeNr || this.selectedUserDetail.employeeNr === 0) {
        return this.selectedUserDetail.employeeNr;
      }
      return "Deze gebruiker heeft geen werknemersnummer";
    },
    showProlongedSickLeaveModal() {
      VoerroModal.show('prolongedSickLeaveModal');
    },
    closeProlongedSickLeaveModal() {
      VoerroModal.hide('prolongedSickLeaveModal');
    },
    startProlongSickLeave(startDate) {
      this.startProlongedSickLeaveForSelectedUser(startDate);
    },
    endProlongSickLeave(endDate) {
      this.endProlongedSickLeaveForSelectedUser(endDate);
    },
    isProlongedSickLeavePeriodStarted() {
      for (let period of this.selectedUserDetail.prolongedSickLeavePeriodSet) {
        if (period.endDate === null) {
          return true;
        }
      }
      return false;
    },
    sort: function (newSortColumn) {
      if (newSortColumn === this.currentSortColumn) {
        this.currentSortDirectionAscending = !this.currentSortDirectionAscending;
      } else {
        this.currentSortDirectionAscending = true;
      }
      this.currentSortColumn = newSortColumn;
    },
    OnHREmployerChange() {
      if (this.selectedUserDetail.employeeNr) {
        alert("Bij het ingeven van een andere HR werkgever moet ook het werknemersnummer aangepast worden.");
      }
    },
    endDateFormatted() {
      if (this.selectedUserDetail.employmentEndDate === undefined || this.selectedUserDetail.employmentEndDate === null) {
        return '';
      }
      return toYearAndMonthAndDay(this.selectedUserDetail.employmentEndDate);
    },
  }
};
</script>

<style scoped>

.text-red {
  color: red;
}

.negative-ml {
  margin-left: -0.761rem;
}

.overviewAndNav {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.remove-button-look {
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}

.remove-button-look:disabled {
  cursor: default;
  opacity: 0.65;
}

.mt-3_2 {
  margin-top: 1.05rem !important;
}

.mt-3_5 {
  margin-top: 1.15rem !important;
}

.mt-3_8 {
  margin-top: 2rem !important;
}

</style>

